import SpeakButton from "@/components/SpeakButton";
import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@clerk/react";
import { Star, Users, ShieldAlert } from "lucide-react";
import { useT } from "@/i18n";
import { ACTIVITIES, AI_TEXT, AI_TOOLS } from "@/components/ailab/content";
import { EditActivity, CharacterActivity, AdMission } from "@/components/ailab/ActivitiesA";
import { VideoActivity, CoachActivity } from "@/components/ailab/ActivitiesB";
import GameTools from "@/components/games/GameTools";
import UsageBadge from "@/components/ailab/UsageBadge";
import DrawActivity from "@/components/ailab/DrawActivity";
import AskLesson from "@/components/ailab/AskLesson";
import Gallery from "@/components/ailab/Gallery";

const PANELS = { draw: DrawActivity, ask: AskLesson, edit: EditActivity, character: CharacterActivity, ad: AdMission, video: VideoActivity, coach: CoachActivity };

export default function AiLab() {
  const { lang } = useT();
  const tx = AI_TEXT[lang];
  const { isSignedIn } = useAuth();
  const [active, setActive] = useState("ad");
  const Panel = PANELS[active];
  const act = ACTIVITIES.find((a) => a.id === active);

  return (
    <section className="learn-bg rounded-[2rem] border-4 border-white shadow-xl p-4 sm:p-8 mt-8 space-y-8" data-testid="ai-lab">
      <div className="text-center">
        <h2 className="font-fredoka text-3xl sm:text-5xl lg:text-6xl font-bold leading-tight" data-testid="ai-lab-title">
          <span className="bubble-text text-white">{tx.titleA} </span>
          <span className="bubble-text text-cyan-300">{tx.titleB}</span>
        </h2>
        <p className="mt-2 inline-block bg-white/90 rounded-full px-4 py-1.5 text-xs sm:text-base font-bold text-blue-900 shadow">{tx.lab}</p>
      </div>
      <div className="rounded-[2rem] overflow-hidden border-4 border-white shadow-xl">
        <img src="/img/ai-scene.webp" alt="Lumi y Spark editando una imagen con IA" className="w-full h-auto block" />
      </div>
      <p className="bg-white/90 rounded-3xl px-5 py-3 font-bold text-blue-900 shadow-md text-center">{tx.intro}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {ACTIVITIES.map((a) => {
          const Icon = a.icon;
          return (
            <motion.button key={a.id} type="button" whileHover={{ y: -3 }} whileTap={{ scale: 0.96 }} onClick={() => setActive(a.id)} className={`bg-white rounded-3xl border-4 ${active === a.id ? "border-yellow-300 ring-4 ring-yellow-200" : "border-white"} shadow-md p-3 flex items-center gap-3 text-left`} data-testid={`ai-activity-${a.id}`}>
              <span className={`${a.color} rounded-2xl p-2.5 shrink-0`}><Icon className="w-6 h-6 text-white" /></span>
              <span className="font-fredoka font-bold text-blue-950 leading-tight">{a[lang]}</span>
            </motion.button>
          );
        })}
      </div>
      <div className="bg-white/70 rounded-[2rem] p-4 sm:p-6" data-testid={`ai-panel-${active}`}>
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <img src={["coach", "video", "ask"].includes(active) ? "/img/spark.webp" : "/img/lumi.webp"} alt="" className="w-14 mascot-wiggle" />
          <h3 className="font-fredoka text-2xl sm:text-3xl font-bold text-blue-950">{act[lang]}</h3>
          <SpeakButton key={`${active}-${lang}`} compact mascot={["coach", "video", "ask"].includes(active) ? "spark" : "lumi"} text={act[lang]} testId="ask-ai-activity" />
          {isSignedIn && <span className="ml-auto"><UsageBadge /></span>}
        </div>
        {isSignedIn || active === "ask" ? <Panel key={`${active}-${lang}`} /> : (
          <Link to="/sign-in" className="glossy-btn inline-block bg-yellow-300 text-blue-900 font-fredoka font-bold text-xl px-8 py-3 rounded-full" data-testid="ai-lab-signin">{tx.signIn}</Link>
        )}
        <p className="mt-5 inline-flex items-center gap-2 bg-rose-50 rounded-full px-4 py-2 text-sm font-bold text-rose-700"><ShieldAlert className="w-4 h-4" /> {tx.notMagic}</p>
      </div>
      {isSignedIn && <Gallery />}
      <div>
        <h3 className="font-fredoka text-xl sm:text-2xl font-bold text-blue-950">{tx.tools}</h3>
        <div><GameTools tools={AI_TOOLS} hideTitle gridClass="sm:grid-cols-2 lg:grid-cols-4" /></div>
      </div>
      <div className="bg-blue-800 rounded-[2rem] px-5 py-5 text-center shadow-xl" data-testid="ai-motto">
        <p className="font-fredoka text-xl sm:text-3xl font-bold text-white inline-flex flex-wrap items-center justify-center gap-2">
          <Star className="w-7 h-7 text-yellow-300 fill-yellow-300" /> {tx.motto1} <span className="text-yellow-300">{tx.motto2}</span> <Star className="w-7 h-7 text-yellow-300 fill-yellow-300" />
        </p>
        <p className="mt-3 inline-flex items-center gap-2 border-2 border-white/60 rounded-full px-4 py-1.5 text-white font-bold text-sm"><Users className="w-4 h-4" /> {tx.adult}</p>
      </div>
    </section>
  );
}
